import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Skeleton } from '@/shared/ui/skeleton';
import {
  fetchPublicInvitePassbook,
  type InviteReferral,
  type PublicInvitePassbook,
  type RegisterInviteResult,
  type RedeemGiftCardResult,
} from '../api/use-public-invite';
import { resolveGiftVariant } from '../model/invite-variant';
import { PublicInviteLayout } from './public-invite-layout';
import { PublicInviteWizard } from './public-invite-wizard';
import { PublicInviteGiftWizard } from './public-invite-gift-wizard';
import { PublicInviteGiftRedeemWizard } from './public-invite-gift-redeem-wizard';
import { PublicInviteSuccess } from './public-invite-success';
import { PublicInviteDuplicate } from './public-invite-duplicate';
import { PublicInviteGiftEmailed } from './public-invite-gift-emailed';
import { PublicInviteGiftNotFound } from './public-invite-gift-not-found';
import { PublicInviteInactive } from './public-invite-inactive';
import { PublicInviteSectionTabs, type InviteSection } from './public-invite-section-tabs';
import { PublicInviteLoyaltyRulesCard } from './public-invite-loyalty-rules-card';

type Phase = 'form' | 'created' | 'duplicate' | 'gift-emailed' | 'gift-not-found';

interface PublicInviteFormFlowProps {
  passbook: PublicInvitePassbook;
  campanha?: string;
  department?: { idOffice: string; idDepartment: string };
  referral?: InviteReferral;
}

/** Decide entre o form normal e os 3 fluxos de cartão-presente (Fase 4), e troca de tela pós-submit. */
function PublicInviteFormFlow({ passbook, campanha, department, referral }: PublicInviteFormFlowProps) {
  const giftVariant = useMemo(() => resolveGiftVariant(passbook), [passbook]);
  const [phase, setPhase] = useState<Phase>('form');
  const [section, setSection] = useState<InviteSection>('cadastro');
  const [registerResult, setRegisterResult] = useState<RegisterInviteResult | null>(null);
  const [redeemResult, setRedeemResult] = useState<RedeemGiftCardResult | null>(null);
  const hasLoyaltyRules = passbook.loyaltyRules.length > 0;

  const onRegisterSuccess = (r: RegisterInviteResult) => {
    setRegisterResult(r);
    if (r.status === 'created') setPhase('created');
    else if (r.status === 'duplicate') setPhase('duplicate');
  };

  const onRedeemSuccess = (r: RedeemGiftCardResult) => {
    setRedeemResult(r);
    if (r.status === 'emailed') setPhase('gift-emailed');
    else if (r.status === 'not-found') setPhase('gift-not-found');
  };

  return (
    <AnimatePresence mode="wait">
      {phase === 'form' ? (
        <motion.div
          key="form"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {referral?.banner && giftVariant === 'none' ? referral.banner : null}
          {hasLoyaltyRules ? (
            <PublicInviteSectionTabs
              active={section}
              onChange={setSection}
              accentColor={passbook.backgroundColor}
              foregroundColor={passbook.foregroundColor}
            />
          ) : null}
          {section === 'regras' ? (
            <PublicInviteLoyaltyRulesCard rules={passbook.loyaltyRules} />
          ) : giftVariant === 'omatuto' ? (
            <PublicInviteGiftWizard
              passbook={passbook}
              campanha={campanha}
              onSuccess={onRegisterSuccess}
            />
          ) : giftVariant === 'trintaeum' || giftVariant === 'trintaeum-livre' ? (
            <PublicInviteGiftRedeemWizard
              passbook={passbook}
              showValorGift={giftVariant === 'trintaeum-livre'}
              campanha={campanha}
              onSuccess={onRedeemSuccess}
            />
          ) : (
            <PublicInviteWizard
              passbook={passbook}
              campanha={campanha}
              department={department}
              referral={referral}
              onSuccess={onRegisterSuccess}
            />
          )}
        </motion.div>
      ) : phase === 'created' && registerResult?.status === 'created' ? (
        <motion.div key="created" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <PublicInviteSuccess
            passbook={passbook}
            serialNumber={registerResult.serialNumber}
            giftMode={giftVariant === 'omatuto'}
          />
        </motion.div>
      ) : phase === 'duplicate' && registerResult?.status === 'duplicate' ? (
        <motion.div key="duplicate" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <PublicInviteDuplicate email={registerResult.email} />
        </motion.div>
      ) : phase === 'gift-emailed' && redeemResult?.status === 'emailed' ? (
        <motion.div key="gift-emailed" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <PublicInviteGiftEmailed email={redeemResult.email} />
        </motion.div>
      ) : phase === 'gift-not-found' ? (
        <motion.div key="gift-not-found" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <PublicInviteGiftNotFound />
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

interface PublicInvitePageProps {
  /** Passbook resolvido fora da URL (link de indicação `/invite/r/:code`). */
  idPassbookOverride?: string;
  /** Indicação do Indique e Ganhe (banner + código no cadastro). */
  referral?: InviteReferral;
}

export function PublicInvitePage({ idPassbookOverride, referral }: PublicInvitePageProps = {}) {
  const params = useParams<{
    idPassbook: string;
    idOffice?: string;
    idDepartment?: string;
  }>();
  const idPassbook = idPassbookOverride ?? params.idPassbook;
  const { idOffice, idDepartment } = params;
  const department = idOffice && idDepartment ? { idOffice, idDepartment } : undefined;
  const [searchParams] = useSearchParams();
  const resumeSerial = searchParams.get('serial');
  // `?campanha=` é lido pra QUALQUER loja no legado (getInvite), não só variações específicas.
  const campanha = searchParams.get('campanha') ?? undefined;

  const [passbook, setPassbook] = useState<PublicInvitePassbook | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!idPassbook) return;
    let cancelled = false;
    setLoading(true);
    fetchPublicInvitePassbook(idPassbook, department)
      .then((data) => {
        if (!cancelled) setPassbook(data);
      })
      .catch(() => {
        if (!cancelled) setNotFound(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `department` é derivado de idOffice/idDepartment, já nas deps
  }, [idPassbook, idOffice, idDepartment]);

  if (!idPassbook)
    return (
      <PublicInviteInactive
        title="Link inválido"
        description="Este link de cadastro está incompleto."
      />
    );

  if (loading) {
    return (
      <div className="container mx-auto max-w-2xl flex-1 px-4 py-10">
        <div className="space-y-6">
          <Skeleton className="mx-auto h-16 w-48" />
          <Skeleton className="h-64 w-full rounded-2xl" />
        </div>
      </div>
    );
  }

  if (notFound || !passbook) {
    return (
      <div className="container mx-auto flex-1 px-4 py-10">
        <PublicInviteInactive
          title="Cadastro não encontrado"
          description="Verifique se o link está correto."
        />
      </div>
    );
  }

  if (passbook.inactive) {
    return (
      <PublicInviteLayout passbook={passbook}>
        <PublicInviteInactive />
      </PublicInviteLayout>
    );
  }

  // Retomada pelo QR de handoff (desktop): cartão já criado, só falta instalar.
  if (resumeSerial) {
    return (
      <PublicInviteLayout passbook={passbook}>
        <PublicInviteSuccess passbook={passbook} serialNumber={resumeSerial} />
      </PublicInviteLayout>
    );
  }

  return (
    <PublicInviteLayout passbook={passbook}>
      <PublicInviteFormFlow passbook={passbook} campanha={campanha} department={department} referral={referral} />
    </PublicInviteLayout>
  );
}
